<template>
  <div class="animated fadeIn callbacks">
    <!-- 5 minutes with nobody picking it up -->
    <b-alert :show="summary.waitingOver5Minutes > 0" variant="danger" class="callbacks__banner">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <strong>{{ summary.waitingOver5Minutes }}</strong>
      {{ summary.waitingOver5Minutes === 1 ? 'request has' : 'requests have' }} waited 5+ minutes with nobody picking it up.
    </b-alert>

    <b-card>
      <div slot="header" class="callbacks__header">
        <div>
          <strong>Call-back requests</strong>
          <small class="text-muted ml-2">From the phone consultation page. Call within 5 minutes, 8am–5pm.</small>
        </div>
        <div class="callbacks__tools">
          <b-form-radio-group v-model="view" :options="viewOptions" buttons button-variant="outline-primary" size="sm" @change="load" />
          <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="load">
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }" aria-hidden="true"></i> Refresh
          </b-button>
          <b-button size="sm" :variant="showAlerts ? 'primary' : 'outline-primary'" :pressed="showAlerts"
                    aria-controls="callback-alerts" @click="toggleAlerts">
            <i class="fa fa-bell-o" aria-hidden="true"></i> Alert settings
          </b-button>
        </div>
      </div>

      <!-- Who gets the alerts: any admin can change it here, no deploy needed -->
      <b-collapse id="callback-alerts" v-model="showAlerts">
        <div class="callbacks__alerts">
          <p class="small text-muted mb-3">
            Who gets the call-back alerts. One email or mobile per line.
            <span v-if="alerts.saved">Last changed by {{ alerts.updatedBy }}, {{ dateTime(alerts.updatedAt) }}.</span>
            <span v-else>Not changed here yet, so the server settings are used.</span>
          </p>
          <b-row>
            <b-col md="6">
              <b-form-group label="Team emails: every new request" label-for="cb-team-emails"
                            :state="alertErrors.teamEmails ? false : null" :invalid-feedback="alertErrors.teamEmails">
                <b-form-textarea id="cb-team-emails" v-model="alertForm.teamEmails" rows="3" max-rows="8"
                                 :state="alertErrors.teamEmails ? false : null" placeholder="name@thegpservice.co.uk" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Team lead emails: request waiting 5 minutes" label-for="cb-lead-emails"
                            :state="alertErrors.leadEmails ? false : null" :invalid-feedback="alertErrors.leadEmails">
                <b-form-textarea id="cb-lead-emails" v-model="alertForm.leadEmails" rows="3" max-rows="8"
                                 :state="alertErrors.leadEmails ? false : null" placeholder="lead@thegpservice.co.uk" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Team WhatsApp numbers" description="Used once WhatsApp is switched on." label-for="cb-team-wa"
                            :state="alertErrors.teamWhatsApp ? false : null" :invalid-feedback="alertErrors.teamWhatsApp">
                <b-form-textarea id="cb-team-wa" v-model="alertForm.teamWhatsApp" rows="2" max-rows="8"
                                 :state="alertErrors.teamWhatsApp ? false : null" placeholder="07700 900123" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Team lead WhatsApp numbers" description="Used once WhatsApp is switched on." label-for="cb-lead-wa"
                            :state="alertErrors.leadWhatsApp ? false : null" :invalid-feedback="alertErrors.leadWhatsApp">
                <b-form-textarea id="cb-lead-wa" v-model="alertForm.leadWhatsApp" rows="2" max-rows="8"
                                 :state="alertErrors.leadWhatsApp ? false : null" placeholder="07700 900123" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-button size="sm" variant="primary" :disabled="alertsBusy" @click="saveAlerts">Save</b-button>
          <b-button size="sm" variant="outline-secondary" class="ml-2" :disabled="alertsBusy" @click="testAlerts">Send test email</b-button>
          <small class="text-muted ml-2">The test goes to the saved team emails.</small>
        </div>
      </b-collapse>

      <b-table :items="requests" :fields="fields" hover outlined responsive show-empty small
               empty-text="No call-back requests to show." :tbody-tr-class="rowClass" @row-clicked="open">
        <template #cell(waiting)="row">
          <span v-if="row.item.waitingMinutes !== null" class="callbacks__wait" :class="waitClass(row.item)">
            {{ row.item.waitingMinutes }} min
          </span>
          <span v-else class="text-muted">{{ time(row.item.createdAt) }}</span>
        </template>
        <template #cell(firstName)="row">
          <strong>{{ row.item.firstName }}</strong>
          <b-badge v-if="row.item.outOfHours" variant="secondary" class="ml-1">Out of hours</b-badge>
        </template>
        <template #cell(mobile)="row">
          <a :href="'tel:' + row.item.mobile" @click.stop>{{ row.item.mobile }}</a>
        </template>
        <template #cell(status)="row">
          <b-badge :variant="statusVariant(row.item.status)">{{ row.item.statusLabel }}</b-badge>
          <small v-if="row.item.attempts" class="text-muted d-block">Attempt {{ row.item.attempts }} of 3</small>
          <small v-if="row.item.nextTryAt" class="text-muted d-block">Try after {{ time(row.item.nextTryAt) }}</small>
        </template>
        <template #cell(pickedUpBy)="row">
          <span v-if="row.item.pickedUpBy">{{ row.item.pickedUpBy }}</span>
          <span v-else class="text-danger font-weight-bold">Nobody yet</span>
        </template>
        <template #cell(actions)="row">
          <b-button size="sm" variant="primary" @click.stop="open(row.item)">Open</b-button>
        </template>
      </b-table>
    </b-card>

    <!-- One request -->
    <b-modal ref="detail" size="xl" hide-footer :title="detail ? 'Call-back: ' + detail.firstName : 'Call-back'" @hidden="closeDetail">
      <div v-if="detail" class="callbacks__detail">
        <b-row>
          <b-col lg="7">
            <!-- Who and what -->
            <dl class="callbacks__facts">
              <div><dt>Name</dt><dd>{{ detail.firstName }}</dd></div>
              <div><dt>Mobile</dt><dd><a :href="'tel:' + detail.mobile">{{ detail.mobile }}</a></dd></div>
              <div><dt>Email</dt><dd>{{ detail.email }}</dd></div>
              <div><dt>About</dt><dd>{{ detail.topicLabel }}</dd></div>
              <div><dt>When</dt><dd>{{ detail.whenLabel }}<span v-if="detail.outOfHours"> · arrived out of hours</span></dd></div>
              <div><dt>Requested</dt><dd>{{ dateTime(detail.createdAt) }}</dd></div>
              <div><dt>Status</dt><dd><b-badge :variant="statusVariant(detail.status)">{{ detail.statusLabel }}</b-badge>
                <span v-if="detail.attempts" class="text-muted"> · attempt {{ detail.attempts }} of 3</span></dd></div>
              <div v-if="detail.bookedSlot"><dt>Booked</dt><dd>{{ detail.bookedSlot }}<span v-if="detail.fee"> · £{{ Number(detail.fee).toFixed(2) }}</span></dd></div>
            </dl>

            <!-- Pick up -->
            <div class="callbacks__pickup">
              <template v-if="!detail.pickedUpByAdminId">
                <b-button variant="success" :disabled="busy" @click="pickUp(false)">
                  <i class="fa fa-hand-paper-o" aria-hidden="true"></i> Pick up
                </b-button>
                <small class="text-muted ml-2">So nobody else calls the same patient.</small>
              </template>
              <template v-else>
                <span>Picked up by <strong>{{ detail.pickedUpBy }}</strong> at {{ time(detail.pickedUpAt) }}</span>
                <b-button v-if="detail.pickedUpByAdminId !== myAdminId" size="sm" variant="outline-warning" class="ml-2"
                          :disabled="busy" @click="takeOver">Take over</b-button>
              </template>
            </div>

            <!-- Call guide (Avishka's procedure) -->
            <b-card no-body class="callbacks__guide">
              <b-card-header header-tag="div" class="p-0">
                <b-button block variant="link" class="text-left" v-b-toggle.call-guide>
                  <i class="fa fa-book" aria-hidden="true"></i> Call guide
                </b-button>
              </b-card-header>
              <b-collapse id="call-guide">
                <b-card-body>
                  <ol>
                    <li>"Hello, it's {{ myName || 'the team' }} from The GP Service. You asked us to call you back about booking an appointment."</li>
                    <li><strong>Check full name and date of birth</strong> before anything else.</li>
                    <li><strong>Urgent symptoms</strong> (chest pain, struggling to breathe, very unwell): tell them to call <strong>999</strong> or <strong>NHS 111</strong>, don't book, mark <em>Escalated</em> and tell the prescriber on duty.</li>
                    <li>No medical advice on this call.</li>
                    <li>Price: <strong>{{ money(standardFee, true) }}</strong>, plus {{ money(documentFee, true) }} only if a prescription, fit note or letter is issued. Medicine is extra.</li>
                    <li>Find or add the patient, then send the payment link (Book Consultation). Never take card numbers over the phone.</li>
                    <li>10% discount only with Avishka's approval.</li>
                    <li>Not ready? "No pressure — you can request another call back or call us on 024 7745 0727."</li>
                  </ol>
                </b-card-body>
              </b-collapse>
            </b-card>

            <!-- Patient: find or add, then book with the payment link -->
            <h6 class="callbacks__h">Patient and booking</h6>
            <div v-if="detail.patientId" class="callbacks__linked">
              <i class="fa fa-check-circle text-success" aria-hidden="true"></i>
              Linked to patient <router-link :to="'/patient/' + detail.patientId">#{{ detail.patientId }}</router-link>.
              <b-button size="sm" variant="primary" class="ml-2" :to="{ path: '/patient/' + detail.patientId, query: { book: '1' } }">
                Book consultation / send payment link
              </b-button>
              <small class="text-muted d-block mt-1">
                The payment link you send this patient is picked up here, and the request turns "Booking confirmed" when it's paid.
              </small>
            </div>
            <div v-else>
              <b-button size="sm" variant="outline-primary" :disabled="searching" @click="findPatient">
                <i class="fa fa-search" aria-hidden="true"></i> Find patient (mobile and email)
              </b-button>
              <b-button size="sm" variant="outline-secondary" class="ml-1" @click="addPatient">
                <i class="fa fa-user-plus" aria-hidden="true"></i> Add patient
              </b-button>
              <div v-if="searched" class="mt-2">
                <p v-if="!matches.length" class="text-muted mb-1">No patient with this mobile or email. Add a new patient.</p>
                <p v-else class="text-muted mb-1">Check the name and date of birth with the caller before choosing (never on email alone).</p>
                <b-table v-if="matches.length" :items="matches" :fields="matchFields" small bordered>
                  <template #cell(choose)="m">
                    <b-button size="sm" variant="success" :disabled="busy" @click="linkPatient(m.item)">This is the patient</b-button>
                  </template>
                </b-table>
              </div>
            </div>

            <!-- Outcome -->
            <h6 class="callbacks__h">Outcome of the call</h6>
            <b-form-textarea v-model="note" rows="2" max-rows="5" placeholder="Note (optional). No card numbers." class="mb-2" />
            <div class="callbacks__outcomes">
              <b-button size="sm" variant="warning" :disabled="busy || closed" @click="recordOutcome('NO_ANSWER')">
                No answer (attempt {{ Math.min(detail.attempts + 1, 3) }} of 3)
              </b-button>
              <b-button size="sm" variant="outline-success" :disabled="busy || closed" @click="recordOutcome('FOLLOW_UP')">Interested – follow-up</b-button>
              <b-button size="sm" variant="outline-primary" :disabled="busy || closed" @click="recordOutcome('CALL_LATER')">Requested call-back</b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="busy || closed" @click="recordOutcome('NP_PRICE')">Not proceeding – price</b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="busy || closed" @click="recordOutcome('NOT_PROCEEDING')">Not proceeding – other</b-button>
              <b-button size="sm" variant="danger" :disabled="busy || closed" @click="recordOutcome('ESCALATED')">Escalated (urgent)</b-button>
            </div>
            <div class="callbacks__booked">
              <b-form-input v-model="bookedSlot" size="sm" placeholder="Booked time, e.g. Today at 2:30pm" />
              <b-form-select v-model="fee" size="sm" class="callbacks__fee" :options="feeOptions" aria-label="Fee" />
              <b-button size="sm" variant="success" :disabled="busy" @click="recordOutcome('BOOKED_PAID')">Booking confirmed</b-button>
              <b-button size="sm" variant="outline-dark" :disabled="busy" @click="recordOutcome('DONE')">Consultation done</b-button>
            </div>
            <b-button size="sm" variant="link" class="px-0" :disabled="busy || !note.trim()" @click="saveNote">Save note only</b-button>
          </b-col>

          <b-col lg="5">
            <h6 class="callbacks__h">History</h6>
            <ul class="callbacks__history">
              <li v-for="(h, i) in historyNewestFirst" :key="'h' + i">
                <span class="text-muted">{{ dateTime(h.at) }}</span> · <strong>{{ h.who }}</strong> · {{ h.what }}
                <div v-if="h.note" class="callbacks__note">{{ h.note }}</div>
              </li>
            </ul>
            <h6 class="callbacks__h">Messages</h6>
            <ul class="callbacks__history">
              <li v-for="(m, i) in (detail.messages || []).slice().reverse()" :key="'m' + i">
                <span class="text-muted">{{ dateTime(m.at) }}</span> · {{ m.to }} · {{ m.channel }} · {{ m.what }} ·
                <b-badge :variant="m.result === 'sent' ? 'success' : (m.result === 'off' ? 'light' : 'danger')">{{ m.result }}</b-badge>
              </li>
              <li v-if="!(detail.messages || []).length" class="text-muted">None yet.</li>
            </ul>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
const REFRESH_MS = 15000;
/**
 * Consultation fee recorded on "Booking confirmed" (Suleman's handover: standard fee, or 10% off with Avishka's approval).
 * The live values come from GET api/admin/pricing (GPS appointmentPrice / documentFee); these are used if that call fails.
 */
const FALLBACK_STANDARD_FEE = 39.00;
const FALLBACK_DOCUMENT_FEE = 19.99; // same fallback as the backend and patient app
const DISCOUNT = 0.10;
const round2 = n => Math.round(n * 100) / 100;
const discounted = fee => round2(fee * (1 - DISCOUNT));

/** Toast wording: the same words as the buttons. */
const OUTCOME_LABELS = {
  NO_ANSWER: 'No answer',
  FOLLOW_UP: 'Interested – follow-up',
  CALL_LATER: 'Requested call-back',
  NP_PRICE: 'Not proceeding – price',
  NOT_PROCEEDING: 'Not proceeding – other',
  ESCALATED: 'Escalated',
  BOOKED_PAID: 'Booking confirmed',
  DONE: 'Consultation done'
};

const CLOSED = ['BOOKED_PAID', 'NP_PRICE', 'NOT_PROCEEDING', 'UNABLE', 'ESCALATED', 'DONE'];

export default {
  name: 'CallbackRequests',
  data () {
    return {
      view: 'open',
      viewOptions: [
        { text: 'To call', value: 'open' },
        { text: 'All', value: 'all' }
      ],
      requests: [],
      summary: { new: 0, waitingOver5Minutes: 0 },
      showAlerts: false,
      alerts: { saved: false },
      alertForm: { teamEmails: '', leadEmails: '', teamWhatsApp: '', leadWhatsApp: '' },
      alertErrors: {},
      alertsBusy: false,
      loading: false,
      timer: null,
      fields: [
        { key: 'waiting', label: 'Waiting' },
        { key: 'firstName', label: 'Name' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'topicLabel', label: 'About' },
        { key: 'whenLabel', label: 'When' },
        { key: 'status', label: 'Status' },
        { key: 'pickedUpBy', label: 'Picked up by' },
        { key: 'actions', label: '' }
      ],
      detail: null,
      busy: false,
      note: '',
      bookedSlot: '',
      fee: FALLBACK_STANDARD_FEE,
      standardFee: FALLBACK_STANDARD_FEE,
      documentFee: FALLBACK_DOCUMENT_FEE,
      /** A fee recorded earlier that matches neither option (e.g. before a price change), kept so it isn't overwritten. */
      recordedFee: null,
      searching: false,
      searched: false,
      matches: [],
      matchFields: [
        { key: 'displayName', label: 'Name' },
        { key: 'dateOfBirth', label: 'Date of birth' },
        { key: 'phone', label: 'Mobile' },
        { key: 'email', label: 'Email' },
        { key: 'choose', label: '' }
      ],
      baseTitle: document.title,
      profile: null
    };
  },
  computed: {
    myAdminId () {
      return this.profile && this.profile.id ? this.profile.id : null;
    },
    myName () {
      const name = this.profile && (this.profile.name || this.profile.firstName);
      return name ? String(name).split(' ')[0] : '';
    },
    closed () {
      return this.detail && CLOSED.includes(this.detail.status);
    },
    discountedFee () {
      return discounted(this.standardFee);
    },
    feeOptions () {
      const options = [
        { value: this.standardFee, text: this.money(this.standardFee, true) + ' standard' },
        { value: this.discountedFee, text: this.money(this.discountedFee) + ' (10% off, Avishka approved)' }
      ];
      if (this.recordedFee !== null) {
        options.push({ value: this.recordedFee, text: this.money(this.recordedFee) + ' (recorded earlier)' });
      }
      return options;
    },
    historyNewestFirst () {
      return ((this.detail && this.detail.history) || []).slice().reverse();
    }
  },
  created () {
    this.$userService.getProfile().then(profile => { this.profile = profile; }).catch(() => {});
    this.$pricingService.getGpsPricing().then(gps => {
      if (!gps) {
        return;
      }
      const standard = Number(gps.appointmentPrice);
      const documentFee = Number(gps.documentFee);
      if (gps.appointmentPrice !== null && gps.appointmentPrice !== undefined && Number.isFinite(standard)) {
        if (this.fee === this.standardFee) {
          this.fee = standard;
        }
        this.standardFee = standard;
      }
      if (gps.documentFee !== null && gps.documentFee !== undefined && Number.isFinite(documentFee)) {
        this.documentFee = documentFee;
      }
    });
    this.load();
    this.timer = setInterval(() => {
      if (!document.hidden) {
        this.load(true);
      }
    }, REFRESH_MS);
  },
  beforeDestroy () {
    clearInterval(this.timer);
    document.title = this.baseTitle;
  },
  methods: {
    /** £39 / £44.10; `short` drops ".00" for whole pounds. */
    money (value, short) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return '';
      }
      return '£' + (short && Number.isInteger(n) ? String(n) : n.toFixed(2));
    },
    /** Session expired (401): use the app's own sign-out, as after 15 minutes of inactivity, instead of an empty list. */
    signedOut (error) {
      if (!this.$callbackService.isSignedOut(error)) {
        return false;
      }
      clearInterval(this.timer);
      this.$root.logout();
      return true;
    },
    failed (error) {
      if (!this.signedOut(error)) {
        this.$awn.alert(this.$callbackService.errorMessage(error));
      }
    },
    toggleAlerts () {
      this.showAlerts = !this.showAlerts;
      if (this.showAlerts) {
        this.loadAlerts();
      }
    },
    showAlertSettings (s) {
      const lines = list => (list || []).join('\n');
      this.alerts = s || { saved: false };
      this.alertForm = {
        teamEmails: lines(this.alerts.teamEmails),
        leadEmails: lines(this.alerts.leadEmails),
        teamWhatsApp: lines(this.alerts.teamWhatsApp),
        leadWhatsApp: lines(this.alerts.leadWhatsApp)
      };
      this.alertErrors = {};
    },
    loadAlerts () {
      this.alertsBusy = true;
      this.$callbackService.settings()
        .then(s => this.showAlertSettings(s))
        .catch(error => this.failed(error))
        .finally(() => { this.alertsBusy = false; });
    },
    alertsChanged () {
      const lines = list => (list || []).join('\n');
      return ['teamEmails', 'leadEmails', 'teamWhatsApp', 'leadWhatsApp']
        .some(k => this.alertForm[k].trim() !== lines(this.alerts[k]).trim());
    },
    saveAlerts () {
      const lines = text => text.split(/[\n,;]+/).map(v => v.trim()).filter(Boolean);
      this.alertsBusy = true;
      this.alertErrors = {};
      this.$callbackService.saveSettings({
        teamEmails: lines(this.alertForm.teamEmails),
        leadEmails: lines(this.alertForm.leadEmails),
        teamWhatsApp: lines(this.alertForm.teamWhatsApp),
        leadWhatsApp: lines(this.alertForm.leadWhatsApp)
      }).then(s => {
        this.showAlertSettings(s);
        this.$awn.success('Alert settings saved');
      }).catch(error => {
        if (this.signedOut(error)) {
          return;
        }
        this.alertErrors = this.$callbackService.fieldErrors(error);
        this.$awn.alert(this.$callbackService.errorMessage(error));
      }).finally(() => { this.alertsBusy = false; });
    },
    testAlerts () {
      if (this.alertsChanged()) {
        this.$awn.warning('Save your changes first. The test goes to the saved team emails.');
        return;
      }
      this.alertsBusy = true;
      this.$callbackService.testSettings().then(r => {
        const count = (r.to || []).length;
        if (r.status === 'sent') {
          this.$awn.success('Test email sent to ' + count + (count === 1 ? ' address' : ' addresses'));
        } else if (r.status === 'off') {
          this.$awn.warning('Email is not set up on this server, so nothing was sent.');
        } else {
          this.$awn.alert('The test email could not be sent. Please try again.');
        }
      }).catch(error => this.failed(error))
        .finally(() => { this.alertsBusy = false; });
    },
    load (quiet) {
      if (quiet !== true) {
        this.loading = true;
      }
      return Promise.all([this.$callbackService.list(this.view), this.$callbackService.summary()])
        .then(([list, summary]) => {
          this.requests = list || [];
          this.summary = summary || { new: 0, waitingOver5Minutes: 0 };
          document.title = (this.summary.new ? '(' + this.summary.new + ') ' : '') + 'Call-backs';
        })
        .catch(error => {
          if (this.signedOut(error)) {
            return;
          }
          if (quiet !== true) {
            this.$awn.alert(this.$callbackService.errorMessage(error, 'Could not load the call-back requests.'));
          }
        })
        .finally(() => { this.loading = false; });
    },
    open (item) {
      this.note = '';
      this.bookedSlot = '';
      this.fee = this.standardFee;
      this.recordedFee = null;
      this.searched = false;
      this.matches = [];
      this.$callbackService.detail(item.id).then(detail => {
        this.detail = detail;
        this.bookedSlot = detail.bookedSlot || '';
        const recorded = detail.fee === null || detail.fee === undefined || detail.fee === '' ? NaN : round2(Number(detail.fee));
        if (!Number.isFinite(recorded) || recorded === this.standardFee) {
          this.fee = this.standardFee;
        } else if (recorded === this.discountedFee) {
          this.fee = this.discountedFee;
        } else {
          this.recordedFee = recorded;
          this.fee = recorded;
        }
        this.$refs.detail.show();
      }).catch(error => this.failed(error));
    },
    closeDetail () {
      this.detail = null;
      this.load(true);
    },
    apply (promise, successText) {
      this.busy = true;
      return promise.then(detail => {
        this.detail = detail;
        if (successText) {
          this.$awn.success(successText);
        }
        this.load(true);
        return detail;
      }).finally(() => { this.busy = false; });
    },
    pickUp (takeOver) {
      return this.apply(this.$callbackService.pickUp(this.detail.id, takeOver), takeOver ? 'You took this over.' : 'Picked up. Please call now.')
        .catch(error => {
          if (this.$callbackService.isConflict(error)) {
            const message = this.$callbackService.errorMessage(error);
            if (window.confirm(message + '. Take it over?')) {
              this.pickUp(true);
            }
            return;
          }
          this.failed(error);
        });
    },
    takeOver () {
      if (window.confirm('Take over this call-back from ' + this.detail.pickedUpBy + '?')) {
        this.pickUp(true);
      }
    },
    recordOutcome (outcome) {
      const payload = { outcome: outcome, note: this.note.trim() || null };
      if (outcome === 'BOOKED_PAID') {
        payload.bookedSlot = this.bookedSlot.trim() || null;
        payload.fee = this.fee;
      }
      if (outcome === 'ESCALATED' && !window.confirm('Escalate? Make sure they were told to call 999 or NHS 111, and tell the prescriber on duty.')) {
        return;
      }
      this.apply(this.$callbackService.outcome(this.detail.id, payload), 'Saved: ' + (OUTCOME_LABELS[outcome] || outcome))
        .then(() => { this.note = ''; })
        .catch(error => this.failed(error));
    },
    saveNote () {
      this.apply(this.$callbackService.addNote(this.detail.id, this.note.trim()), 'Note saved')
        .then(() => { this.note = ''; })
        .catch(error => this.failed(error));
    },
    /** Every patient matching the mobile (either format) or the email; the admin chooses after checking name and DOB. */
    findPatient () {
      this.searching = true;
      const mobileDigits = String(this.detail.mobile || '').replace(/^0/, '');
      Promise.all([
        this.$patientService.search(mobileDigits, 0, 10).catch(() => null),
        this.$patientService.search(this.detail.email, 0, 10, { email: true }).catch(() => null)
      ]).then(results => {
        const seen = {};
        this.matches = results
          .filter(r => r && r.content)
          .reduce((all, r) => all.concat(r.content), [])
          .filter(p => (seen[p.id] ? false : (seen[p.id] = true)));
        this.searched = true;
      }).finally(() => { this.searching = false; });
    },
    linkPatient (patient) {
      this.apply(this.$callbackService.linkPatient(this.detail.id, patient.id), 'Linked to ' + (patient.displayName || 'the patient'))
        .catch(error => this.failed(error));
    },
    /** Opens Add patient on the Patients page with first name, mobile and email filled in (nothing in the URL). */
    addPatient () {
      try {
        sessionStorage.setItem('callbackPrefill', JSON.stringify({
          firstName: this.detail.firstName,
          phone: this.detail.mobile,
          email: this.detail.email
        }));
      } catch (e) { /* storage blocked: the form opens empty */ }
      this.$refs.detail.hide();
      this.$router.push({ path: '/patients', query: { add: '1' } });
    },
    waitClass (item) {
      if (item.waitingMinutes >= 15) return 'is-red';
      if (item.waitingMinutes >= 5) return 'is-amber';
      return '';
    },
    rowClass (item) {
      return item && item.waitingMinutes !== null && item.waitingMinutes >= 5 && !item.pickedUpBy ? 'callbacks__row--late' : '';
    },
    statusVariant (status) {
      switch (status) {
        case 'NEW': return 'primary';
        case 'BOOKED_PAID': return 'success';
        case 'DONE': return 'dark';
        case 'NO_ANSWER': return 'warning';
        case 'ESCALATED': return 'danger';
        case 'UNABLE': case 'NP_PRICE': case 'NOT_PROCEEDING': return 'secondary';
        default: return 'info';
      }
    },
    /** Java sends "2026-10-15T17:17:01+01:00[Europe/London]"; browsers can't read the "[zone]" part. */
    toDate (iso) {
      return new Date(String(iso).replace(/\[[^\]]*\]$/, ''));
    },
    time (iso) {
      if (!iso) return '';
      return this.toDate(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
    },
    dateTime (iso) {
      if (!iso) return '';
      return this.toDate(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
    }
  }
};
</script>

<style scoped>
.callbacks__header { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; }
.callbacks__tools { display: flex; gap: 8px; align-items: center; }
.callbacks__banner { display: flex; gap: 8px; align-items: center; }
.callbacks__wait { font-weight: 600; }
.callbacks__wait.is-amber { color: #b26a00; }
.callbacks__wait.is-red { color: #c0392b; }
.callbacks__facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 24px; margin-bottom: 16px; }
.callbacks__facts dt { font-size: 12px; color: #73818f; font-weight: 600; text-transform: uppercase; }
.callbacks__facts dd { margin: 0; }
.callbacks__pickup { padding: 12px; background: #f0f3f5; border-radius: 6px; margin-bottom: 16px; }
.callbacks__guide { margin-bottom: 16px; }
.callbacks__guide ol { margin: 0; padding-left: 18px; }
.callbacks__guide li { margin-bottom: 6px; }
.callbacks__h { margin: 16px 0 8px; font-weight: 700; }
.callbacks__outcomes, .callbacks__booked { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.callbacks__booked .form-control { max-width: 260px; }
.callbacks__booked .callbacks__fee { max-width: 240px; }
.callbacks__alerts { border: 1px solid #e4e7ea; border-radius: 4px; background: #f9fafb; padding: 12px 16px; margin-bottom: 16px; }
.callbacks__alerts textarea { font-size: 13px; }
.callbacks__history { list-style: none; padding: 0; margin: 0 0 16px; max-height: 320px; overflow-y: auto; font-size: 13px; }
.callbacks__history li { padding: 6px 0; border-bottom: 1px solid #e4e7ea; }
.callbacks__note { margin-top: 2px; padding: 4px 8px; background: #fff8e1; border-radius: 4px; white-space: pre-wrap; }
.callbacks__linked { padding: 10px 12px; background: #e9f7ef; border-radius: 6px; }
</style>

<style>
.callbacks__row--late { background: #fdecea !important; cursor: pointer; }
.callbacks .table tbody tr { cursor: pointer; }
</style>
